/* assets/product-info.js — final */

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('.quantity__input');
        this.currentVariant = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('[type="submit"]');

        // cache
        this.quantityForm = null;
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        if (!this.input) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        // Cart update => refresh quantity rules
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }

        // Variant change => Dawn Pub/Sub (NOT a DOM event)
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            const sectionId = this.dataset.originalSection
              ? this.dataset.originalSection
              : this.dataset.section;

            // Ignore events for other product sections
            if (event.data.sectionId !== sectionId) return;

            // event.data.html can be string or Document
            const htmlDoc = this.parseToDoc(event.data.html);

            // Update quantity rules (existing Dawn behavior)
            this.updateQuantityRules(event.data.sectionId, htmlDoc);

            // ✅ NEW: replace inventory meter(s) from fresh server HTML
            this.replaceInventoryMetersFromHtml(htmlDoc);

            // Re-evaluate bounds
            this.setQuantityBoundries();
          }
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      /* -------- NEW HELPERS (for inventory meter) -------- */

      // Normalize string/Document to a Document
      parseToDoc(htmlOrDoc) {
        try {
          if (htmlOrDoc && typeof htmlOrDoc.getElementById === 'function') {
            return htmlOrDoc;
          }
          const parser = new DOMParser();
          const str =
            typeof htmlOrDoc === 'string'
              ? htmlOrDoc
              : String(htmlOrDoc || '');
          return parser.parseFromString(str, 'text/html');
        } catch (e) {
          console.warn('parseToDoc failed', e);
          // Fallback: empty doc
          return new DOMParser().parseFromString('<div></div>', 'text/html');
        }
      }

      // Take fresh section HTML and swap only the .inventory-meter nodes
      replaceInventoryMetersFromHtml(htmlOrDoc) {
        try {
          const doc = this.parseToDoc(htmlOrDoc);

          // Fresh meters from server-rendered HTML
          const freshMeters = doc.querySelectorAll('.inventory-meter');
          if (!freshMeters || !freshMeters.length) return;

          // Current meters only within this product-info scope
          const currentMeters = this.querySelectorAll('.inventory-meter');
          if (!currentMeters || !currentMeters.length) return;

          const n = Math.min(currentMeters.length, freshMeters.length);
          for (let i = 0; i < n; i++) {
            currentMeters[i].replaceWith(freshMeters[i]);
          }
        } catch (e) {
          console.warn('Inventory meter replace failed', e);
        }
      }

      /* -------------- Dawn stock code (kept) -------------- */

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input.dataset.cartQuantity
            ? parseInt(this.input.dataset.cartQuantity)
            : 0,
          min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
          max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
          step: this.input.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.input.min = min;
        this.input.max = max;
        this.input.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        if (!this.currentVariant || !this.currentVariant.value) return;

        const spinner = this.querySelector(
          '.quantity__rules-cart .loading__spinner'
        );
        if (spinner) spinner.classList.remove('hidden');

        fetch(
          `${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`,
          { credentials: 'same-origin' }
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            // Update qty rules (existing)
            this.updateQuantityRules(this.dataset.section, html);

            // ✅ NEW: also refresh inventory meter(s)
            this.replaceInventoryMetersFromHtml(html);

            // bounds re-calc
            this.setQuantityBoundries();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            const spinner2 = this.querySelector(
              '.quantity__rules-cart .loading__spinner'
            );
            if (spinner2) spinner2.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, htmlOrDoc) {
        const html = this.parseToDoc(htmlOrDoc);
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated || !this.quantityForm) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }
    }
  );
}

/* NOTE:
   - We intentionally DO NOT add any document.addEventListener('variant:change', …)
     because Dawn themes emit Pub/Sub events instead (handled above).
*/
